import React, { createContext, useContext } from 'react'
import { Components, ExtraProps } from 'react-markdown'

import { ExtendedChatMessage } from '../../../enviroment/store/chatStore'
import { cn } from '../../../enviroment/lib/utils'
import { useCachedMarkdown } from '../../../enviroment/hooks/useCachedMarkdown'
import AssistantMessageWrapper from './AssistantMessageWrapper'
import ThinkingIndicator from './ThinkingIndicator'
import ThinkingTrace from './ThinkingTrace'
import UpgradeMessage from './messages/UpgradeMessage'
import AreaLimitMessage from './messages/AreaLimitMessage'
import WhiteLabelMessage from './messages/WhiteLabelMessage'
import ExportLayersMessage from './messages/ExportLayersMessage'
import CreateProjectMessage from './messages/CreateProjectMessage'
import MiomboReportMessage from './messages/MiomboReportMessage'
import HtmlReportMessage, { HtmlReportLoader } from './messages/HtmlReportMessage'
import { hasHtmlBlock } from '../../../enviroment/utils/hasHtmlBlock'

type LinkClickHandler = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => void

// Cached markdown trees are shared across mounts, so links read the handler from context instead of closing over it.
const LinkClickContext = createContext<LinkClickHandler>(() => undefined)

type HtmlBlockMode = 'code' | 'streaming' | 'report'

// Cached trees are keyed by text alone, so the role and stream state can't live in the components map.
const HtmlBlockContext = createContext<HtmlBlockMode>('code')

const MarkdownLink: Components['a'] = ({ href, children }) => {
  const onLinkClick = useContext(LinkClickContext)

  if (!href || !href.trim()) {
    return <span className="text-gray-400">{children}</span>
  }
  return (
    <a
      href={href}
      className="text-blue-400 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => onLinkClick(e, href)}
    >
      {children}
    </a>
  )
}

const readHtmlBlock = (pre: ExtraProps['node']): string | null => {
  const code = pre?.children[0]
  if (code?.type !== 'element' || code.tagName !== 'code') return null

  const classNames = (code.properties.className ?? []) as string[]
  if (!classNames.some((name) => name.toLowerCase() === 'language-html')) return null

  const text = code.children[0]
  return text?.type === 'text' ? text.value : ''
}

const MarkdownPre: Components['pre'] = ({ node, children }) => {
  const mode = useContext(HtmlBlockContext)
  const html = readHtmlBlock(node)

  if (mode === 'code' || html === null) return <pre>{children}</pre>
  if (mode === 'streaming') return <HtmlReportLoader />
  return <HtmlReportMessage html={html} />
}

const MARKDOWN_COMPONENTS: Components = {
  h1: ({ children }) => <h1 className="mb-3 mt-5 text-2xl font-bold">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-2 mt-4 text-xl font-bold">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-2 mt-3 text-lg font-semibold">{children}</h3>,
  h4: ({ children }) => <h4 className="mb-1 mt-2 text-base font-semibold">{children}</h4>,
  strong: ({ children }) => <strong className="font-semibold text-gray-100">{children}</strong>,
  em: ({ children }) => <em className="italic text-gray-200">{children}</em>,
  ul: ({ children }) => <ul className="ml-4 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="ml-4 list-decimal space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-gray-100">{children}</li>,
  a: MarkdownLink,
  p: ({ children }) => <p className="mb-3 whitespace-pre-wrap break-words last:mb-0">{children}</p>,
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-gray-700">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-700">{children}</thead>,
  tr: ({ children }) => <tr className="border-b border-gray-700 last:border-b-0">{children}</tr>,
  th: ({ children, style }) => (
    <th style={style} className="px-3 py-2 text-left font-semibold text-gray-100">
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td style={style} className="px-3 py-2 align-top text-gray-100">
      {children}
    </td>
  ),
  pre: MarkdownPre,
  code: ({ children }) => <code className="rounded bg-gray-700 px-1 py-0.5 font-mono text-sm">{children}</code>,
}

interface MessageMarkdownProps {
  content: string
  isLast?: boolean
  htmlBlockMode: HtmlBlockMode
  onLinkClick: LinkClickHandler
}

const MessageMarkdown = ({ content, isLast, htmlBlockMode, onLinkClick }: MessageMarkdownProps) => {
  // The last message may still be streaming, and caching every chunk would evict the finished messages.
  const markdownTree = useCachedMarkdown(content, MARKDOWN_COMPONENTS, !isLast)
  return (
    <LinkClickContext.Provider value={onLinkClick}>
      <HtmlBlockContext.Provider value={htmlBlockMode}>{markdownTree}</HtmlBlockContext.Provider>
    </LinkClickContext.Provider>
  )
}

interface ChatMessageComponentProps {
  message: ExtendedChatMessage
  isLast?: boolean
  isStreaming?: boolean
  onLinkClick: LinkClickHandler
  onMinimize?: () => void
}

const ChatMessageComponent: React.FC<ChatMessageComponentProps> = ({
  message,
  isLast,
  isStreaming,
  onLinkClick,
  onMinimize,
}) => {
  const content = typeof message.content === 'string' ? message.content : JSON.stringify(message.content)

  if (message.role === 'assistant' && message.type && message.type !== 'text') {
    const specialContent = (() => {
      switch (message.type) {
        case 'upgrade':
          return (
            <UpgradeMessage
              currentUsers={message.metadata?.currentUsers}
              maxUsers={message.metadata?.maxUsers}
              onMinimize={onMinimize}
            />
          )
        case 'area-limit':
          return <AreaLimitMessage onMinimize={onMinimize} />
        case 'white-label':
          return <WhiteLabelMessage onMinimize={onMinimize} />
        case 'export-layers':
          return <ExportLayersMessage onMinimize={onMinimize} />
        case 'create-project':
          return <CreateProjectMessage message={content} onMinimize={onMinimize} />
        case 'miombo-report':
          return <MiomboReportMessage />
        default:
          return null
      }
    })()

    if (specialContent) {
      return <AssistantMessageWrapper>{specialContent}</AssistantMessageWrapper>
    }
  }

  const thinkingSteps = message.thinkingSteps ?? []
  const hasThinking = message.role === 'assistant' && thinkingSteps.length > 0
  const isHtmlReport = message.role === 'assistant' && hasHtmlBlock(content)
  const htmlBlockMode: HtmlBlockMode = message.role !== 'assistant' ? 'code' : isStreaming ? 'streaming' : 'report'

  if (message.role === 'assistant' && message.isLoading) {
    return (
      <AssistantMessageWrapper transparent hideIcon>
        <ThinkingIndicator
          steps={thinkingSteps}
          startedAt={Date.parse(message.date)}
          showElapsed={!message.hideElapsed}
        />
      </AssistantMessageWrapper>
    )
  }

  const messageBody = (
    <div
      className={cn(
        'rounded-lg p-3',
        isHtmlReport ? 'w-full' : 'max-w-80',
        message.role === 'user'
          ? 'bg-gray-700 text-gray-100'
          : message.isError
          ? 'border border-red-700 bg-red-900/50 text-gray-100'
          : 'bg-gray-800 text-gray-100',
      )}
    >
      <MessageMarkdown content={content} isLast={isLast} htmlBlockMode={htmlBlockMode} onLinkClick={onLinkClick} />
      {message.citations && message.citations.length > 0 && (
        <div className="mt-2 border-t border-gray-700 pt-2">
          <p className="text-xs text-gray-400">Sources</p>
          <ul className="mt-1 space-y-1">
            {message.citations.map((citation, idx) => (
              <li key={idx}>
                <a
                  href={citation}
                  className="text-xs text-blue-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => onLinkClick(e, citation)}
                >
                  {citation}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )

  if (message.role === 'assistant') {
    if (hasThinking) {
      return (
        <AssistantMessageWrapper transparent>
          <ThinkingTrace steps={thinkingSteps} durationMessage={message.thinkingDuration} />
          <div className="mt-1">{messageBody}</div>
        </AssistantMessageWrapper>
      )
    }
    return <AssistantMessageWrapper wide={isHtmlReport}>{messageBody}</AssistantMessageWrapper>
  }

  return <div className="flex items-center justify-end gap-3">{messageBody}</div>
}

export default React.memo(ChatMessageComponent)
